/**
 * @fileoverview API client with request deduplication, caching, and auth handling
 * @module lib/apiClient
 *
 * USAGE:
 * import apiClient from './lib/apiClient';
 * const data = await apiClient.get('/api/users');
 *
 * DEPENDENCIES:
 * - ./logger
 */

import logger from './logger';
import { isNative, clearAuthStorage, redirectToSignedOut, API_BASE } from '../platform';

// BASE_URL resolution:
//   1. Explicit REACT_APP_API_BASE wins (e.g., for one-off testing)
//   2. Native iOS shell: ALWAYS the deployed backend. The Capacitor webview
//      serves from capacitor://localhost, so hostname==="localhost" is true
//      there too — without the isNative guard the client would treat native
//      as local dev and fetch API paths from the app bundle (returning
//      index.html, not JSON → "e.sort is not a function" on first data load).
//   3. On real localhost (web dev), use empty string → relative paths →
//      CRA setupProxy.js forwards to the local backend at :10000.
//   4. Otherwise (production / preview deploys), point at Render.
let BASE_URL = API_BASE;

// pluggable token getter
let tokenGetter = getAuthTokenFromStorage;
let supabaseInstance = null;

// Auth retry state
let isRefreshing = false;
let failedQueue = [];
const MAX_RETRY_COUNT = 3;
const retryCount = new Map();

export function initApiClient({ supabase, baseUrl } = {}) {
  if (baseUrl) BASE_URL = baseUrl;
  if (supabase) {
    supabaseInstance = supabase;
    if (supabase?.auth?.getSession) {
      tokenGetter = async () => {
        try {
          // First try Supabase's getSession
          const { data, error } = await supabase.auth.getSession();
          if (data?.session?.access_token) {
            logger.auth.debug('Got token from Supabase getSession');
            return data.session.access_token;
          }
          
          // Fallback to localStorage
          return getAuthTokenFromStorage();
        } catch (err) {
          logger.auth.error('Error in tokenGetter', null, err);
          return getAuthTokenFromStorage();
        }
      };
    }
  }
}

// src/lib/apiClient.js - Update just the getAuthTokenFromStorage function

function getAuthTokenFromStorage() {
  try {
    // Your specific Supabase project key
    const supabaseKey = 'sb-tekstak-auth-token';
    const tokenData = localStorage.getItem(supabaseKey);
    
    if (tokenData) {
      try {
        const parsed = JSON.parse(tokenData);
        if (parsed?.access_token) {
          logger.auth.debug('Found Supabase token in specific key');
          return parsed.access_token;
        }
      } catch (e) {
        logger.auth.error('Error parsing Supabase token', null, e);
      }
    }
    
    // Fallback: check all sb- keys (in case the key changes)
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith("sb-") && key.endsWith("-auth-token")) {
        const raw = localStorage.getItem(key);
        if (!raw || key === supabaseKey) continue; // Skip if already checked
        try {
          const parsed = JSON.parse(raw);
          if (parsed?.access_token) {
            logger.auth.debug('Found token in fallback key', { key });
            return parsed.access_token;
          }
        } catch (e) {
          logger.auth.error('Error parsing token from storage', { key }, e);
        }
      }
    }
    
    // Other fallbacks
    const authObj = localStorage.getItem("auth");
    if (authObj) {
      try {
        const parsed = JSON.parse(authObj);
        return parsed.access_token || parsed.session?.access_token || null;
      } catch (e) {
        logger.auth.error('Error parsing auth object', null, e);
      }
    }

    logger.auth.debug('No token found in any location');
    return null;
  } catch (error) {
    logger.auth.error('Error getting auth token', null, error);
    return null;
  }
}

// Process queued requests after token refresh
function processQueue(error, token = null) {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
}

// Refresh token using Supabase
async function refreshAccessToken() {
  if (!supabaseInstance) {
    throw new Error('No Supabase instance for token refresh');
  }

  try {
    const { data, error } = await supabaseInstance.auth.refreshSession();
    if (error) throw error;
    return data?.session?.access_token;
  } catch (error) {
    logger.auth.error('Token refresh failed', null, error);
    throw error;
  }
}

export function toQueryParams(obj = {}) {
  const qs = new URLSearchParams();
  const append = (k, v) => {
    if (v == null) return;
    if (Array.isArray(v)) { 
      v.forEach((x) => append(k, x)); 
      return; 
    }
    if (v instanceof Date) { 
      qs.append(k, v.toISOString()); 
      return; 
    }
    qs.append(k, String(v));
  };
  Object.entries(obj).forEach(([k, v]) => append(k, v));
  return qs;
}

async function parseResponse(res, url) {
  const ct = res.headers.get("content-type") || "";
  
  if (!res.ok) {
    // Special handling for 401
    if (res.status === 401) {
      const error = new Error('Unauthorized');
      error.status = 401;
      error.url = url;
      
      if (ct.includes("application/json")) {
        try {
          const data = await res.json();
          error.code = data.code || 'UNAUTHORIZED';
          error.details = data;
        } catch {}
      }
      throw error;
    }

    // Account awaiting admin approval. Every endpoint 403s for these users, so
    // without this the app renders as a wall of failed panels with no
    // explanation. Announced as an event rather than a redirect because the
    // native shell has no server behind capacitor://localhost — a hard
    // navigation to a route path fails to load there (see
    // platform/redirectToSignedOut). A listener in App renders the screen.
    if (res.status === 403 && ct.includes("application/json")) {
      let pendingErr = null;
      try {
        const data = await res.clone().json();
        if (data.code === 'ACCOUNT_PENDING') {
          pendingErr = new Error(data.error || 'Your account is awaiting approval.');
          pendingErr.status = 403;
          pendingErr.code = 'ACCOUNT_PENDING';
          pendingErr.url = url;
        }
      } catch {
        // Body wasn't readable JSON — fall through to normal error handling.
      }
      if (pendingErr) {
        window.dispatchEvent(new CustomEvent('tekstak:account-pending'));
        throw pendingErr;
      }
    }
    
    if (ct.includes("application/json")) {
      const err = await res.json().catch(() => ({}));
      const msg = err?.error || err?.message || `${res.status} ${res.statusText}`;
      const error = new Error(msg);
      error.status = res.status;
      // Attach the full response body so callers can inspect structured
      // error payloads (e.g. regression_gate_blocked returns conflict
      // analysis + samples that the UI needs to render an override panel).
      error.data = err;
      throw error;
    }
    
    const text = await res.text();
    const error = new Error(`HTTP ${res.status} at ${url}: ${text.slice(0, 250)}`);
    error.status = res.status;
    throw error;
  }
  
  return ct.includes("application/json") ? res.json() : res.text();
}

// ---- dedupe + micro-cache ----
const inflight = new Map();
const cache = new Map();
const CACHE_MS = 500;

function makeKey(method, url, body) {
  const bodyKey = body && typeof body === "object" && !(body instanceof FormData)
    ? JSON.stringify(body)
    : body || "";
  return `${method}:${url}:${bodyKey}`;
}

async function getToken() {
  try { 
    return tokenGetter ? await tokenGetter() : null; 
  } catch { 
    return null; 
  }
}

// Network-level timeout. Without this, a hung connection (e.g. backend
// redeploy 502 storm) leaves the fetch promise pending forever, which
// then pins the inflight-dedup entry and freezes every subsequent
// poll for the same key. 45s comfortably exceeds the slowest healthy
// endpoint we have.
const DEFAULT_TIMEOUT_MS = 45000;

// Single doFetch function - no duplication
async function doFetch(url, { method, body, headers, signal, credentials, skipAuth = false, timeoutMs = DEFAULT_TIMEOUT_MS }) {
  const token = skipAuth ? null : await getToken();
  const isJson = body && typeof body === "object" && !(body instanceof FormData);

  // Log API request
  logger.api.request(method, url, { hasToken: !!token });

  // Compose caller-provided signal (if any) with our timeout-based abort.
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);
  const onCallerAbort = () => timeoutController.abort();
  if (signal) {
    if (signal.aborted) timeoutController.abort();
    else signal.addEventListener('abort', onCallerAbort, { once: true });
  }

  // Dev-bypass header (Lesson 2 follow-up). When set, the backend's
  // verifyAuth middleware skips real token validation and treats the
  // request as admin. Triple-guarded server-side: (1) header present,
  // (2) matches env-set token, (3) NODE_ENV !== 'production'. Frontend
  // only sends it from localhost; production builds never see this env
  // var so the header is never attached in real deployments.
  // !isNative: the Capacitor shell serves from capacitor://localhost, so a
  // bare hostname check is TRUE there and the app attached this header to
  // every production request. X-Dev-Bypass-Token is not in the backend's CORS
  // allowedHeaders, so the preflight failed and every call died with an
  // opaque "Load failed" before leaving the device. It also meant the native
  // build was shipping a super-admin bypass token at production (INF-1).
  const isLocalhost = !isNative && typeof window !== 'undefined'
    && window.location.hostname === 'localhost';
  const devBypassToken = isLocalhost ? process.env.REACT_APP_DEV_BYPASS_TOKEN : null;

  try {
    const res = await fetch(url, {
      method,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(devBypassToken ? { 'X-Dev-Bypass-Token': devBypassToken } : {}),
        ...(isJson ? { "Content-Type": "application/json" } : {}),
        ...headers,
      },
      body: isJson ? JSON.stringify(body) : body,
      signal: timeoutController.signal,
      credentials,
    });

    // Log API response
    logger.api.response(method, url, res.status, 0);

    return await parseResponse(res, url);
  } catch (err) {
    if (err && err.name === 'AbortError') {
      // Distinguish caller-cancel from our timeout.
      const wasTimeout = !signal || !signal.aborted;
      const e = new Error(wasTimeout ? `Request timeout after ${timeoutMs}ms: ${method} ${url}` : `Request aborted: ${method} ${url}`);
      e.code = wasTimeout ? 'TIMEOUT' : 'ABORTED';
      e.url = url;
      throw e;
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
    if (signal) signal.removeEventListener('abort', onCallerAbort);
  }
}

async function request(path, {
  method = "GET",
  body,
  query,
  headers,
  signal,
  // WKWebView refuses credentialed cross-origin requests from a
  // capacitor:// scheme origin — they fail at the network layer with a bare
  // "Load failed" before any HTTP response, which is why every API call died
  // in the iOS shell while a plain fetch to the same host succeeded.
  // Auth here is a Bearer token in a header, not cookies, so credentials are
  // unnecessary on native. Web keeps "include" (unchanged behaviour).
  credentials = isNative ? "omit" : "include",
  skipAuth = false,
  timeoutMs,
  _retry = false
} = {}) {
  // Empty BASE_URL (used in local dev so setupProxy can intercept) is
  // not a valid URL base — fall back to window.location.origin so
  // relative paths resolve same-origin. The proxy still picks them up.
  const u = new URL(path, BASE_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost'));
  if (query) {
    const qp = query instanceof URLSearchParams ? query : toQueryParams(query);
    u.search = qp.toString();
  }
  const url = u.toString();
  const key = makeKey(method, url, body);
  const now = Date.now();

  // Don't cache auth endpoints
  const isAuthEndpoint = path.includes('/auth/') || path.includes('/login') || path.includes('/logout');
  // MUTATIONS ARE NEVER CACHED OR DEDUPED. An identical PATCH/POST
  // repeated inside the cache window (qty 2→3→2→3, re-clicking a term
  // pill, double-clicking duplicate) must hit the server every time —
  // serving the cached response silently drops the write.
  const isCacheable = method === 'GET' && !isAuthEndpoint;

  // micro-cache: return recent success within window (GETs only)
  if (isCacheable && !_retry) {
    const hit = cache.get(key);
    if (hit && now - hit.ts < CACHE_MS) return hit.data;
  }

  // inflight dedupe: share the same promise for identical GETs
  if (isCacheable && !_retry && inflight.has(key)) return inflight.get(key);

  // Check retry count to prevent infinite loops
  const currentRetryCount = retryCount.get(url) || 0;
  if (currentRetryCount >= MAX_RETRY_COUNT) {
    retryCount.delete(url);
    const error = new Error('Max retry attempts reached');
    error.code = 'MAX_RETRIES';
    throw error;
  }

  const p = doFetch(url, { method, body, headers, signal, credentials, skipAuth, timeoutMs })
    .then((data) => {
      // Clear retry count on success
      retryCount.delete(url);

      if (isCacheable) {
        cache.set(key, { ts: Date.now(), data });
      }
      return data;
    })
    .catch(async (error) => {
      // Handle 401 errors with retry logic
      if (error.status === 401 && !_retry && !isAuthEndpoint && !skipAuth) {
        // Increment retry count
        retryCount.set(url, currentRetryCount + 1);
        
        if (isRefreshing) {
          // Wait for ongoing refresh
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            // Retry with new token
            return request(path, { 
              method, body, query, headers, signal, credentials,
              _retry: true 
            });
          });
        }

        isRefreshing = true;

        try {
          // Try to refresh the token
          const newToken = await refreshAccessToken();
          
          if (newToken) {
            processQueue(null, newToken);
            
            // Retry the original request
            return request(path, { 
              method, body, query, headers, signal, credentials,
              _retry: true 
            });
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          
          // Clear auth and redirect to login ONCE.
          // try/catch, not just .catch(): if signOut is missing or throws
          // synchronously the TypeError escapes this handler, so
          // clearAuthStorage() never runs and the redirect never happens —
          // the user keeps a dead token and sees the TypeError instead of a
          // login page. A failure to sign out must not cost us the cleanup.
          if (supabaseInstance) {
            try {
              await supabaseInstance.auth?.signOut?.();
            } catch (signOutError) {
              logger.auth.error('signOut failed while clearing a dead session', null, signOutError);
            }
          }

          // Clear auth storage on BOTH platforms. On native the session lives
          // in Capacitor Preferences, not localStorage — clearing only the
          // latter left the dead token in place and every retry 401'd again.
          await clearAuthStorage();

          // Only redirect if not already on login/auth pages
          const currentPath = window.location.pathname;
          if (!currentPath.includes('/login') &&
              !currentPath.includes('/auth') &&
              !currentPath.includes('/signin') &&
              !currentPath.includes('/signup')) {
            // Platform-aware: "/login" is a real route on the web, but a
            // missing file under capacitor://localhost (which is what made an
            // expired session render as "Error Loading Application").
            redirectToSignedOut();
          }

          throw refreshError;
        } finally {
          isRefreshing = false;
        }
      }
      
      // For non-401 errors or if retry failed, just throw
      throw error;
    })
    .finally(() => {
      inflight.delete(key);
    });

  if (isCacheable) inflight.set(key, p);
  return p;
}

// Clear retry counts periodically to prevent memory leak
setInterval(() => {
  retryCount.clear();
}, 60000); // Clear every minute

export const apiClient = {
  get baseUrl() { return BASE_URL; },
  getToken,  // Expose for raw fetch calls (e.g., file downloads)
  get: (path, opts) => {
  // Handle params in opts
  if (opts?.params) {
    const params = new URLSearchParams();
    Object.entries(opts.params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    if (queryString) {
      // Add to path or merge with existing query
      path = path.includes('?') ? `${path}&${queryString}` : `${path}?${queryString}`;
    }
    // Remove params from opts since we've added them to the URL
    const { params: _, ...restOpts } = opts;
    return request(path, { ...restOpts, method: "GET" });
  }
  return request(path, { ...opts, method: "GET" });
},
  post: (path, body, opts) => request(path, { ...opts, method: "POST", body }),
  put: (path, body, opts) => request(path, { ...opts, method: "PUT", body }),
  // PATCH was missing until 2026-08-17 while nine TasksPage handlers called
  // it — every one threw "apiClient.patch is not a function" (review-queue
  // keep-as-is, origin keys, goal edits). Method + callers now agree.
  patch: (path, body, opts) => request(path, { ...opts, method: "PATCH", body }),
  del: (path, opts) => request(path, { ...opts, method: "DELETE" }),
  request,
  
  // Add method to clear auth state (useful for logout)
  clearAuth: () => {
    retryCount.clear();
    failedQueue = [];
    isRefreshing = false;
  }
};

export default apiClient;